// DEPENDENCIES ---------------------------------------------------------------- //

import { Moves } from '@gql/base-types';

import { css } from '@emotion/css';
import { Theme, useTheme, Paper, Typography } from '@mui/material';
import { BiUser } from 'react-icons/bi';

import InfoItem from '@components/InfoItem';

// HELPERS ---------------------------------------------------------------- //

const getContactsByType = (move?: Moves) => {
  const pickupContacts = move?.contactstomoves?.filter(ctm => ctm.contact_type === 'pickup') || [];
  const deliveryContacts = move?.contactstomoves?.filter(ctm => ctm.contact_type === 'delivery') || [];

  return { pickupContacts, deliveryContacts };
};

// TYPES ---------------------------------------------------------------- //

interface MoveDetailsContactsProps {
  move?: Moves;
  isPrinting?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveDetailsContacts = ({ move, isPrinting }: MoveDetailsContactsProps) => {
  const theme = useTheme();
  const cls = useStyles(theme, { isPrinting });

  const { pickupContacts, deliveryContacts } = getContactsByType(move);

  // Don't show if no contacts or if it's a concierge move
  if (!move?.contactstomoves?.length || move?.consumer_pickup) {
    return null;
  }

  return (
    <div className={cls.contacts}>
      <Paper variant={isPrinting ? `customPrint` : `custom`} sx={{ padding: `16px` }}>
        <div className={cls.contactsGrid}>
          {/* PICKUP CONTACTS */}
          <div className={cls.contactSection}>
            <Typography variant='h6' sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BiUser size={20} />
              Pickup Contacts
            </Typography>

            {pickupContacts.length > 0 ? (
              pickupContacts.map((ctm, index) => (
                <div key={ctm.id} className={cls.contactCard}>
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 2,
                      mb: index < pickupContacts.length - 1 ? 1 : 0,
                      backgroundColor: theme.palette.grey[50],
                    }}
                  >
                    <InfoItem keyText='Name' valueText={ctm.contact?.name || 'N/A'} />
                    {ctm.contact?.mobile_number && (
                      <InfoItem keyText='Mobile' valueText={ctm.contact.mobile_number} />
                    )}
                    {ctm.contact?.landline_number && (
                      <InfoItem
                        keyText='Landline'
                        valueText={ctm.contact.landline_number}
                      />
                    )}
                    {ctm.contact?.email && (
                      <InfoItem keyText='Email' valueText={ctm.contact.email} />
                    )}
                    {ctm.contact?.notes && <InfoItem keyText='Notes' valueText={ctm.contact.notes} />}
                    {ctm.is_primary && (
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'inline-block',
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          mt: 1,
                        }}
                      >
                        Primary Contact
                      </Typography>
                    )}
                  </Paper>
                </div>
              ))
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No pickup contacts
              </Typography>
            )}
          </div>

          {/* DELIVERY CONTACTS */}
          <div className={cls.contactSection}>
            <Typography variant='h6' sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BiUser size={20} />
              Delivery Contacts
            </Typography>

            {deliveryContacts.length > 0 ? (
              deliveryContacts.map((ctm, index) => (
                <div key={ctm.id} className={cls.contactCard}>
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 2,
                      mb: index < deliveryContacts.length - 1 ? 1 : 0,
                      backgroundColor: theme.palette.grey[50],
                    }}
                  >
                    <InfoItem keyText='Name' valueText={ctm.contact?.name || 'N/A'} />
                    {ctm.contact?.mobile_number && (
                      <InfoItem keyText='Mobile' valueText={ctm.contact.mobile_number} />
                    )}
                    {ctm.contact?.landline_number && (
                      <InfoItem
                        keyText='Landline'
                        valueText={ctm.contact.landline_number}
                      />
                    )}
                    {ctm.contact?.email && (
                      <InfoItem keyText='Email' valueText={ctm.contact.email} />
                    )}
                    {ctm.contact?.notes && <InfoItem keyText='Notes' valueText={ctm.contact.notes} />}
                    {ctm.is_primary && (
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'inline-block',
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          mt: 1,
                        }}
                      >
                        Primary Contact
                      </Typography>
                    )}
                  </Paper>
                </div>
              ))
            ) : (
              <Typography variant='body2' color='text.secondary'>
                No delivery contacts
              </Typography>
            )}
          </div>
        </div>
      </Paper>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme, props?: { isPrinting?: boolean }) => ({
  contacts: css`
    margin-bottom: ${theme?.spacing(3)};
  `,
  contactsGrid: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${theme?.spacing(3)};

    ${props?.isPrinting
      ? `
      grid-template-columns: 1fr;
      gap: ${theme?.spacing(2)};
    `
      : ''}

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: ${theme?.spacing(2)};
    }
  `,
  contactSection: css`
    display: flex;
    flex-direction: column;
  `,
  contactCard: css`
    margin-bottom: ${theme?.spacing(1)};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MoveDetailsContacts;
